import Store from 'apps/teacher/store';
import { getLeftNavDashboardVersion, IsSchoolUiDecoupledEnabled } from 'utility/constants';
import { sendErrorToSentry } from '../ErrorBoundary/sendErrorToSentry';
import DataUtility from 'utility/data';

window.app_version = BUILD_VERSION;
window.trackAnalyticsEventInterceptor_school_ui_initailized = false;

const getOnboardingProperties = () => {
    try {
        const state = Store.getState();
        const klassOnboardingPropertiesFromSession = DataUtility.safeJsonParse(sessionStorage.getItem(`onboarding_klass_properties_${state?.currentKlass?.klass?.id}`), {})?.mongo_klass_property;
        const klassProperties = state?.currentKlass?.klass_properties?.mongo_klass_property || klassOnboardingPropertiesFromSession;
        const teacherOnboardingPropertiesFromSession = DataUtility.safeJsonParse(sessionStorage.getItem(`onboarding_teacher_properties_${state?.teacherProfile?.teacher?.id}`), {});
        const teacherProperties = state?.teacherProfile?.teacher || teacherOnboardingPropertiesFromSession;
        const abTests = state?.abTestData?.assigned_ab_tests || {};
        const isNewOnboardingVariant = ['variant', 'variant-a', 'variant-b'].includes(abTests?.["Teacher::NewOnboarding"]?.toLowerCase() || '');
        return isNewOnboardingVariant ? ({
            ["Teacher Role"]: teacherProperties?.teacher_role || teacherOnboardingPropertiesFromSession?.teacher_role || "not_set",
            ["Survey Grade"]: teacherProperties?.survey_grade || teacherOnboardingPropertiesFromSession?.survey_grade || ["not_set"],
            ["Survey Goal"]: teacherProperties?.survey_goal || teacherOnboardingPropertiesFromSession?.survey_goal || "not_set",
            ["Survey Pain Point"]: teacherProperties?.survey_pain_point || teacherOnboardingPropertiesFromSession?.survey_pain_point || "not_set",
            ["Survey Home School Usage"]: klassProperties?.survey_home_school_usage || klassOnboardingPropertiesFromSession?.survey_home_school_usage || "not_set",
            ["Survey Subject"]: klassProperties?.survey_subject || klassOnboardingPropertiesFromSession?.survey_subject || "not_set",
            ["Onboarding Class Size"]: klassProperties?.onboarding_class_size || klassOnboardingPropertiesFromSession?.onboarding_class_size || "not_set",
            ["Bundle Status"]: klassProperties?.bundle_status || klassOnboardingPropertiesFromSession?.bundle_status || "not_set"
        }) : ({});
    } catch (e) {
        console.error(e);
    }

    return {};
}

// Original trackAnalyticsEvent (from web-core-ui) captured once; must never point to interceptor_temp itself
let originalTrackAnalyticsEvent = null;

const interceptor_temp = (eventName, properties = {}, ...args) => {
    try {
        const onboardingProperties = getOnboardingProperties();
        // this will override superproperties in web-core-ui
        window.customSuperProperties = { ...(window.customSuperProperties || {}), ...onboardingProperties };
        let productFlow = sessionStorage.getItem('product_flow');
        if (productFlow && properties) {
            properties['Product Flow'] = productFlow;
        }
        if (properties) {
            properties['App Version'] = BUILD_VERSION;
            properties['Left Nav Dashboard Version'] = getLeftNavDashboardVersion();
            properties['SUI Decoupled'] = IsSchoolUiDecoupledEnabled();
        }
        const combinedProperties = {
            ...properties,
            ...onboardingProperties
        }
        originalTrackAnalyticsEvent?.(eventName, combinedProperties, ...args);
        window.trackAnalyticsEventInterceptor_school_ui_initailized = true;
        if (!IS_PROD) {
            console.log("Events sent via trackAnalyticsEventInterceptor_school_ui_initailized", eventName, combinedProperties, ...args);
        }
    } catch (e) {
        console.error(e);
        sendErrorToSentry("trackAnalyticsEventInterceptor failed", { eventName, properties })
    }
}

const init_trackAnalyticsEventInterceptor_school_ui = () => {
    try {
        if (!window.trackAnalyticsEvent || window.trackAnalyticsEvent === interceptor_temp) {
            return;
        }
        originalTrackAnalyticsEvent = window.trackAnalyticsEvent;
        window.trackAnalyticsEvent = interceptor_temp;
    } catch (e) {
        console.error(e);
    }
}

init_trackAnalyticsEventInterceptor_school_ui();

var avoInitProps = {
    klassGrade: window.KLASS_GRADE?.code ?? null,
    currentGrade: window.KLASS_GRADE?.code ?? null,
    subjectCode: window.KLASS_DETAIL?.id ? "math" : '',
    klass: window.KLASS_DETAIL,
};

export const dispatchAvoEvent = ({ eventName, props, teacherCoreProperties = true, userCoreProperties = true, studentCoreProperties = false }, initProps = avoInitProps) => {
    if (!window.trackAnalyticsEventInterceptor_school_ui_initailized || (window.trackAnalyticsEvent !== interceptor_temp)) {
        init_trackAnalyticsEventInterceptor_school_ui();
    }
    if (eventName) {
        const onboardingProperties = getOnboardingProperties();
        // this will override superproperties in web-core-ui
        window.customSuperProperties = { ...(window.customSuperProperties || {}), ...onboardingProperties };
        let teacherCoreProps = {}, studentCoreProps = {}, userCoreProps = {};

        // add teacher core properties
        if (teacherCoreProperties) {
            let klass_grade = initProps?.klassGrade;
            let current_grade = initProps?.currentGrade;
            teacherCoreProps = {
                grade: klass_grade?.toLowerCase() || "NA",
                teacherType: initProps?.teacherType || "Basic",
                isGradeDifferent: !(current_grade?.toString().toLowerCase() === klass_grade?.toString().toLowerCase()),
                class_: initProps?.klass?.name || initProps?.klass?.klassName || "",
                subject: initProps?.subjectCode || "math"
            }
        }

        if (studentCoreProperties) {
            let klass_grade = initProps?.klassGrade;
            let current_grade = initProps?.currentGrade;
            studentCoreProps = {
                grade: klass_grade?.toLowerCase() || "NA",
                mode: "school",
                mathGrade: current_grade?.toLowerCase() || "NA",
                elaGrade: current_grade?.toLowerCase() || "NA"
            }
        }

        // add user core properties (already being passed)
        if (userCoreProperties) {
            userCoreProps = {
                platform: window?.eventInitProps?.platform || initProps?.platform || "web",
                userType: window?.eventInitProps?.userType || initProps?.userType || "Teacher",
                signupFlow: "classroom_linked"
            }
        }

        props = {
            ...teacherCoreProps,
            ...studentCoreProps,
            ...userCoreProps,
            ...props,
        }

        try {
            if (typeof window.sendEventViaAvoWrapper === 'function') {
                window.sendEventViaAvoWrapper(eventName, props, (APP_MODE === "production") ? 'prod' : 'dev', "school-ui");
            }
            console.group("SCHOOL UI EVENT DISPATCHED")
            console.log(eventName);
            console.log(props);
            console.groupEnd()
        } catch (e) {
            //  if for some reason events are going through
            console.group("SCHOOL UI EVENT DISPATCH FAILED")
            console.log(eventName);
            console.log(props);
            console.warn("Avo Error", e?.message)
            console.groupEnd()
        }
    }
}


